/** Fluxo: a planilha. Dias em linhas, colunas Entrada · Saída · Diário · Economia · Saldo.
 *
 * É a essência do método: ver o que está planejado, o que aconteceu de verdade e a previsão
 * do diário, dia a dia, com o saldo corrido. Tocar num dia abre os lançamentos; o lápis
 * edita no lugar, com escopo quando a linha vem de uma recorrência.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../components/Toast";
import { useSearchParams } from "react-router";
import { Tape } from "../components/Tape";
import { Icon } from "../components/Icon";
import {
  AccountTag,
  Empty,
  ErrorBanner,
  Face,
  Field,
  FieldGroup,
  Key,
  Label,
  Skeleton,
  Stamp,
  zoneClass,
} from "../components/ui";
import { addMonths, column, dayMonth, dayOnly, hojeLocal, isWeekend, money, monthLabel, parseMoney, short, stampLabel, statusLabels, sumMoney, weekday } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { useSimulation } from "../lib/simulation";
import {
  useAccounts,
  useDeleteTransaction,
  useMonthProjection,
  useTransactionsOfMonth,
  useUpdateTransaction,
} from "../lib/queries";
import type { Day, DayItem, Scope, Transaction } from "../lib/types";

/** O nome da coluna, que é o que a pessoa lê. "Economia" só quando é economia de verdade:
 *  pagar fatura ou parcela também é transferência no banco, e não é dinheiro guardado. */
const LABEL_COLUNA: Record<"in" | "out" | "dy" | "sv", string> = {
  in: "Entrada",
  out: "Saída",
  dy: "Diário",
  sv: "Economia",
};

export function Fluxo() {
  const { scope } = usePrefs();
  const sim = useSimulation();
  const [params, setParams] = useSearchParams();
  const hoje = hojeLocal();
  const mes = params.get("mes") ?? hoje.slice(0, 7) + "-01";
  const aberto = params.get("dia");

  const { data, isPending, error, refetch } = useMonthProjection(mes, scope, sim.scenarioId);
  const { data: txs } = useTransactionsOfMonth(mes, sim.scenarioId);
  const { data: accounts } = useAccounts();
  const accById = useMemo(() => new Map((accounts ?? []).map((a) => [a.id, a])), [accounts]);
  const txById = useMemo(() => new Map((txs ?? []).map((t) => [t.id, t])), [txs]);

  const setMes = (novo: string) => {
    const p = new URLSearchParams(params);
    p.set("mes", novo);
    p.delete("dia");
    setParams(p);
  };
  const toggleDia = (dia: string) => {
    const p = new URLSearchParams(params);
    if (aberto === dia) p.delete("dia");
    else p.set("dia", dia);
    setParams(p, { replace: true });
  };

  // Os totais do mês somam os MESMOS itens que as colunas de cada dia, pela mesma regra.
  // Antes o cabeçalho vinha dos agregados do motor e as linhas dos itens, com definições
  // diferentes de bucket: "Saídas" incluía o diário (o motor põe `daily` dentro de
  // `expense`) enquanto a coluna "Saída" do dia excluía, e somar a coluna das 30 linhas
  // nunca dava o número do topo. "Economia" era o líquido no topo e o bruto na linha.
  const totais = useMemo(() => {
    const t = { in: 0, out: 0, dy: 0, sv: 0 };
    for (const dia of data?.days ?? []) {
      for (const item of dia.items) {
        const c = column(item);
        t[c] = sumMoney([t[c], item.amount]);
      }
    }
    return t;
  }, [data]);

  if (isPending) return <section className="view on"><Skeleton lines={8} /></section>;
  if (error)
    return (
      <section className="view on">
        <ErrorBanner message={(error as Error).message} onRetry={() => refetch()} />
      </section>
    );
  if (!data) return null;

  const desembolso = sumMoney([totais.out, totais.dy, totais.sv]);
  const performance = sumMoney([totais.in, -desembolso]);

  return (
    <section className="view on" aria-label="Fluxo">
      <div className="fluxo">
        <div className="monthbar">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Key icon="chevronLeft" title="Mês anterior" onClick={() => setMes(addMonths(mes, -1))} />
            <h2>{monthLabel(mes)}</h2>
            <Key icon="chevronRight" title="Próximo mês" onClick={() => setMes(addMonths(mes, 1))} />
          </div>
          <Label dim>planejado + realizado</Label>
        </div>

        <div className="totals">
          <div>
            <Label>Entradas</Label>
            <span className="v t-in">+{money(totais.in)}</span>
          </div>
          <div>
            <Label>Saídas</Label>
            <span className="v t-crit">−{money(totais.out)}</span>
          </div>
          <div>
            <Label>Diário</Label>
            <span className="v t-att">−{money(totais.dy)}</span>
          </div>
          <div>
            <Label>Economia</Label>
            <span className="v t-abn">−{money(totais.sv)}</span>
          </div>
          <div>
            <Label>Desembolso</Label>
            <span className="v">−{money(desembolso)}</span>
          </div>
          <div className="perf">
            <Label>Performance</Label>
            <span className={`v ${performance >= 0 ? "t-ok" : "t-crit"}`}>
              {performance >= 0 ? "+" : "−"}
              {money(Math.abs(performance))}
            </span>
          </div>
        </div>

        <Face paper className="tape">
          <div className="head">
            <Label>Saldo no mês</Label>
            <Label dim>fim de cada dia</Label>
          </div>
          <Tape days={data.days} today={data.today} />
        </Face>

        <div>
          <div className="colhead">
            <Label>Dia</Label>
            <Label>Entrada</Label>
            <Label>Saída</Label>
            <Label>Diário</Label>
            <Label>Economia</Label>
            <Label>Saldo</Label>
          </div>
          <div className="days">
            {data.days.map((dia) => (
              <DiaLinha
                key={dia.date}
                dia={dia}
                hoje={data.today}
                aberto={aberto === dia.date}
                onToggle={() => toggleDia(dia.date)}
                accById={accById}
                txById={txById}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function DiaLinha({
  dia,
  hoje,
  aberto,
  onToggle,
  accById,
  txById,
}: {
  dia: Day;
  hoje: string;
  aberto: boolean;
  onToggle: () => void;
  accById: Map<string, { name: string; color: string | null }>;
  txById: Map<string, Transaction>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isHoje = dia.date === hoje;
  const passado = dia.date < hoje;

  useEffect(() => {
    if (aberto) ref.current?.scrollIntoView({ block: "nearest" });
  }, [aberto]);

  const reais = dia.items.filter((i) => !i.synthetic);
  const col = (c: "in" | "out" | "dy" | "sv") =>
    sumMoney(reais.filter((i) => column(i) === c).map((i) => i.amount));
  const valores = { in: col("in"), out: col("out"), dy: col("dy") + dia.daily_budget, sv: col("sv") };

  const mini = (v: number, cls: string, sinal: string, aprox = false) =>
    v > 0 ? (
      <span className={cls}>
        {aprox ? "≈" : ""}
        {sinal}
        {short(v)}
      </span>
    ) : (
      <span className="none">—</span>
    );

  return (
    <div
      ref={ref}
      className={`day z-${zoneClass(dia.color)} ${passado ? "past" : ""} ${isHoje ? "today" : ""} ${aberto ? "open" : ""}`}
      data-date={dia.date}
    >
      <button className="row" aria-expanded={aberto} onClick={onToggle}>
        <div className={`dn ${isWeekend(dia.date) ? "weekend" : ""}`}>
          {isHoje && (
            <svg className="needle-mark" viewBox="0 0 7 26" aria-hidden="true">
              <path d="M0 0h7L3.5 6z" />
              <line x1="3.5" y1="6" x2="3.5" y2="26" />
            </svg>
          )}
          {weekday(dia.date)}
          <b>{dayOnly(dia.date)}</b>
        </div>
        <div className="mini">
          {mini(valores.in, "in", "+")}
          {mini(valores.out, "out", "−")}
          {mini(valores.dy, "dy", "−", dia.date > hoje && dia.daily_budget > 0)}
          {mini(valores.sv, "sv", "−")}
        </div>
        <div className={`bal z-${zoneClass(dia.color)}`}>
          {money(dia.end)}
          {isHoje && <small>hoje</small>}
        </div>
      </button>

      <div className="detail">
        <div>
          <div className="cols4">
            <div>
              <Label dim>Entrada</Label>
              <span className="v t-in">{valores.in ? "+" + money(valores.in) : "—"}</span>
            </div>
            <div>
              <Label dim>Saída</Label>
              <span className="v t-crit">{valores.out ? "−" + money(valores.out) : "—"}</span>
            </div>
            <div>
              <Label dim>Diário</Label>
              <span className="v t-att">{valores.dy ? "−" + money(valores.dy) : "—"}</span>
            </div>
            <div>
              <Label dim>Economia</Label>
              <span className="v t-abn">{valores.sv ? "−" + money(valores.sv) : "—"}</span>
            </div>
          </div>

          <div className="items">
            {aberto &&
              reais.map((item) => (
                <ItemLinha
                  key={item.tx_id}
                  item={item}
                  tx={txById.get(item.tx_id)}
                  acc={accById.get(item.account_id)}
                />
              ))}
            {aberto && dia.daily_budget > 0 && (
              <div className="item ghost">
                <div className="what">
                  <div className="t">
                    <span className="kind dy">Diário</span>
                    <span>{dia.date === hoje ? "Diário restante hoje" : "Diário previsto"}</span>
                    <Stamp text="Previsto" cls="plan" />
                  </div>
                  <small>orçamento do dia, definido em Ajustes</small>
                </div>
                <span className="amt">−{money(dia.daily_budget)}</span>
              </div>
            )}
            {aberto && reais.length === 0 && dia.daily_budget === 0 && (
              <Empty title="Dia vazio">Nenhum lançamento em {dayMonth(dia.date)}.</Empty>
            )}
            {aberto && <NovoNoDia data={dia.date} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function NovoNoDia({ data }: { data: string }) {
  const sim = useSimulation();
  return (
    <a className="add" href={`/lancar?dia=${data}&volta=${encodeURIComponent("/fluxo?dia=" + data)}`}>
      <Icon name="plus" />
      Lançar em {dayMonth(data)}
      {sim.active ? " (simulado)" : ""}
    </a>
  );
}

function ItemLinha({
  item,
  tx,
  acc,
}: {
  item: DayItem;
  tx?: Transaction;
  acc?: { name: string; color: string | null };
}) {
  const [editando, setEditando] = useState(false);
  const stamp = stampLabel({
    status: item.status,
    estimated: item.estimated,
    kind: item.kind,
    bucket: item.bucket,
    overdue: item.overdue,
    scenario_id: tx?.scenario_id,
  });
  const simulado = !!tx?.scenario_id;
  const fantasma = item.status === "planned";

  const meta = tx?.recurrence_id
    ? "recorrência"
    : tx?.installment_plan_id
      ? `parcela ${tx.installment_no}/${tx.installment_total}`
      : tx?.card_statement_id && tx.source === "statement_payment"
        ? "pagamento de fatura"
        : (tx?.category_name ?? "");

  return (
    <div className={`item ${simulado ? "sim" : ""} ${fantasma ? "ghost" : ""} ${editando ? "editing" : ""}`}>
      <div className="what">
        <div className="t">
          <span className={`kind ${column(item)}`}>
            {LABEL_COLUNA[column(item)]}
          </span>
          <span>{item.description || "Sem descrição"}</span>
          <Stamp text={stamp.text} cls={stamp.cls} />
        </div>
        <small>
          {acc && <AccountTag name={acc.name} color={acc.color} />} {meta}
        </small>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span className={`amt ${item.kind === "income" ? "t-in" : ""}`}>
          {item.kind === "income" ? "+" : "−"}
          {money(item.amount)}
        </span>
        <Key icon="pen" title="Editar" onClick={() => setEditando((v) => !v)} />
      </div>
      {editando && tx && <Editor tx={tx} onClose={() => setEditando(false)} />}
    </div>
  );
}

/** Edição no lugar: descrição, valor, data, status, escopo da recorrência e excluir. */
function Editor({ tx, onClose }: { tx: Transaction; onClose: () => void }) {
  const aviso = useToast();
  const [confirmando, setConfirmando] = useState(false);
  const update = useUpdateTransaction();
  const remove = useDeleteTransaction();
  const [desc, setDesc] = useState(tx.description);
  const [valor, setValor] = useState(tx.amount.toFixed(2).replace(".", ","));
  const [data, setData] = useState(tx.settles_on);
  const [status, setStatus] = useState(tx.status === "cleared" ? "cleared" : "pending");
  const [escopo, setEscopo] = useState<Scope>("single");

  const recorrente = !!tx.recurrence_id;
  const labels = statusLabels(tx.kind, tx.bucket);
  const amount = parseMoney(valor);

  const salvar = () => {
    update.mutate(
      {
        id: tx.id,
        scope: escopo,
        body: {
          description: desc,
          amount: amount > 0 ? amount : undefined,
          settles_on: data !== tx.settles_on ? data : undefined,
          status: status as "pending" | "cleared",
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="edit">
      <Field label="Descrição" className="f">
        <input value={desc} onChange={(e) => setDesc(e.target.value)} />
      </Field>
      <Field label="Valor" className="f">
        <input className="num" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} />
      </Field>
      <Field label="Data" className="f">
        <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
      </Field>
      <FieldGroup label="Status" className="f">
        <div className="seg small" role="group" aria-label="Status">
          <button type="button" aria-pressed={status === "pending"} onClick={() => setStatus("pending")}>
            {labels.pending}
          </button>
          <button type="button" aria-pressed={status === "cleared"} onClick={() => setStatus("cleared")}>
            {labels.cleared}
          </button>
        </div>
      </FieldGroup>

      {recorrente && (
        <FieldGroup label="Aplicar em" className="f scopes">
          <div className="seg small" role="group" aria-label="Escopo">
            <button type="button" aria-pressed={escopo === "single"} onClick={() => setEscopo("single")}>
              Só esta
            </button>
            <button type="button" aria-pressed={escopo === "from_here"} onClick={() => setEscopo("from_here")}>
              Esta e próximas
            </button>
            <button type="button" aria-pressed={escopo === "all"} onClick={() => setEscopo("all")}>
              Toda a regra
            </button>
          </div>
        </FieldGroup>
      )}

      <div className="acts2">
        {/* Dois passos, como já acontece ao excluir conta e categoria. Este apagava no
            primeiro toque, sem diálogo e sem desfazer, com o botão a 44px de "Cancelar" —
            e o próprio auditor destruiu dois lançamentos por engano assim. */}
        {!confirmando ? (
          <Key danger disabled={remove.isPending} onClick={() => setConfirmando(true)}>
            Excluir
          </Key>
        ) : (
          <Key
            danger
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(
                { id: tx.id, scope: recorrente && escopo === "from_here" ? "from_here" : "single" },
                {
                  onSuccess: () => {
                    aviso.ok(`${tx.description} excluído`);
                    onClose();
                  },
                  onError: (e) => aviso.erro((e as Error).message),
                },
              )
            }
          >
            {remove.isPending ? "Excluindo…" : "Confirmar exclusão"}
          </Key>
        )}
        <Key onClick={onClose}>Cancelar</Key>
        <Key primary onClick={salvar} disabled={update.isPending || !(amount > 0)}>
          Salvar
        </Key>
      </div>
    </div>
  );
}
